// src/pages/pedidos/ModalLocalizacaoPedido.js
import React, { useEffect, useState } from 'react';
import { FiCheck, FiCopy, FiExternalLink, FiMapPin } from 'react-icons/fi';
import ModalContainer from '../../components/modalForm/ModalContainer';
import ModalHeader from '../../components/modalForm/ModalHeader';
import ModalBody from '../../components/modalForm/ModalBody';
import ModalFooter from '../../components/modalForm/ModalFooter';
import ModalActions, { ModalActionButton } from '../../components/modalForm/ModalActions';
import { Pedido } from '../../models/pedidos/Pedido';
import { copiarTexto } from '../../utils/TextoUtils';
import { useAlert } from '../../contexts/geral/AlertContext';
import './ModalLocalizacaoPedido.css';

// Exibe a localização (link do Google Maps) que o cliente enviou pelo App de Delivery.
const ModalLocalizacaoPedido = ({ pedido, onClose }) => {
  const { showAlert } = useAlert();
  const [copiado, setCopiado] = useState(false);
  const show = Pedido.possuiLocalizacao(pedido);
  const localizacao = pedido?.localizacao?.trim() || '';
  const urlMapa = Pedido.montarUrlMapaLocalizacao(localizacao);

  useEffect(() => {
    setCopiado(false);
  }, [pedido?.id]);

  useEffect(() => {
    if (!copiado) return undefined;
    const temporizador = setTimeout(() => setCopiado(false), 2500);
    return () => clearTimeout(temporizador);
  }, [copiado]);

  const handleCopiar = async () => {
    const sucesso = await copiarTexto(localizacao);
    if (sucesso) {
      setCopiado(true);
    } else {
      showAlert('Não foi possível copiar a localização. Selecione o link e copie manualmente.', 'danger');
    }
  };

  const subtitulo = [pedido?.numeroPedido ? `Pedido #${pedido.numeroPedido}` : null, pedido?.nomeCliente]
    .filter(Boolean)
    .join(' · ');

  return (
    <ModalContainer
      show={show}
      variant="cadastro"
      onHide={onClose}
      largura="620px"
      contentClassName="localizacao-pedido-modal modal-operacional"
    >
      <ModalHeader title="Localização do cliente" subtitle={subtitulo} icon={FiMapPin} onHide={onClose} />

      <ModalBody>
        <div className="localizacao-pedido">
          {pedido?.enderecoCliente && (
            <p className="localizacao-pedido-endereco">
              <FiMapPin aria-hidden="true" />
              <span>{pedido.enderecoCliente}</span>
            </p>
          )}

          {urlMapa ? (
            <div className="localizacao-pedido-mapa">
              <iframe
                title={`Mapa da localização do pedido ${pedido?.numeroPedido ?? ''}`.trim()}
                src={urlMapa}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          ) : (
            <div className="localizacao-pedido-sem-mapa">
              Não foi possível exibir o mapa desta localização. Use o link abaixo para abrir no Google Maps.
            </div>
          )}

          <div className="localizacao-pedido-link">
            <span className="localizacao-pedido-link-rotulo">Link da localização</span>
            <a href={localizacao} target="_blank" rel="noopener noreferrer" title="Abrir no Google Maps">
              {localizacao}
            </a>
          </div>
        </div>
      </ModalBody>

      <ModalFooter>
        <ModalActions>
          <ModalActionButton action="close" onClick={onClose}>
            Fechar
          </ModalActionButton>

          <a
            className="btn btn-outline-secondary modal-action"
            href={localizacao}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FiExternalLink aria-hidden="true" />
            <span>Abrir no Google Maps</span>
          </a>

          <button
            type="button"
            className={`btn ${copiado ? 'btn-success' : 'btn-primary'} modal-action localizacao-pedido-copiar`}
            onClick={handleCopiar}
            aria-live="polite"
          >
            {copiado ? <FiCheck aria-hidden="true" /> : <FiCopy aria-hidden="true" />}
            <span>{copiado ? 'Localização copiada!' : 'Copiar localização'}</span>
          </button>
        </ModalActions>
      </ModalFooter>
    </ModalContainer>
  );
};

export default ModalLocalizacaoPedido;
